import { z } from 'zod';
import * as R from 'remeda';
import { keys, unique } from 'remeda';
import { NestedObjectOfStrings } from './utils.js';
import { ShiftType, StateAbbreviation, StateLicenseOption, ENLC_STATES } from './misc.js';
import { ObjectIdString, ActivityDataEntityType, DurationLikeObject } from './common.js';

/**
 * Wraps a schema, converting null inputs to the `.default()` value.
 * Maintains the same output type, keeping it compatible with our LLM JSON schema conversion requirements.
 *
 * @param schema - A Zod schema with a default value
 */
function nullishWithDefault<T extends z.ZodDefault>(schema: T) {
  const defaultValue = schema.def.defaultValue;
  return schema.or(z.null().transform(() => defaultValue)).pipe(schema);
}

//--------------------------------------------------------------------------------------------------------------------//
//region Opportunity Detection

const ConfidenceScore = z.number().int().min(0).max(10);

export const OpportunityDetectionBase = z.object({
  justification: z
    .string()
    .describe(
      'Analyze the candidate data and determine whether the requested piece of information applies. Provide specific details and/or direct quotes from the activity data to support your reasoning.'
    ),
  confidence: ConfidenceScore.describe(
    'Now, based on your reasoning above, assign a confidence score from 0-10, with a score of 10 meaning that the information is definitely applicable to the candidate and 0 meaning it does not apply.'
  ),
  descriptions: z.object({
    manager: z.object({
      description: z
        .string()
        .describe(
          'Manager-facing description of the situation. Observational, third-person perspective (e.g., "The candidate...", "The recruiter..."). Focus on what the manager needs to monitor or act on.'
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Manager-facing version of descriptions.manager.description.'
        ),
    }),
    recruiter: z.object({
      description: z
        .string()
        .describe(
          'Direct, actionable summary written for the recruiter who personally owns this candidate relationship. Use second person where natural (e.g., "Your candidate...", "You should..."). Focus on what the recruiter needs to know or do.'
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Recruiter-facing version of descriptions.recruiter.description.'
        ),
    }),
  }),
});
export type OpportunityDetectionBase = z.infer<typeof OpportunityDetectionBase>;

export const InquiryType = z.enum([
  'Pay/Rate Questions',
  'Job Details and Schedule',
  'Personal/General Questions',
  'Verification Questions',
  'Documentation Request',
  'Benefits/Insurance',
]);
export type InquiryType = z.infer<typeof InquiryType>;

export const QuestionDetectionResult = OpportunityDetectionBase.extend({
  detectedQuestion: z
    .string()
    .describe(
      'Provide the most important question asked by the candidate, quoted verbatim from the attached Activity Data JSON.'
    ),
  inquiryType: InquiryType.nullish().describe(
    "Categorize the type of inquiry based on the content of the candidate's question."
  ),
  urgencyLevel: z
    .enum(['Low', 'Medium', 'Urgent'])
    .nullish()
    .describe(
      'Assess urgency based on business importance: Low (general/conversational), Medium (important but not blocking), or Urgent (mission-critical/blocking).'
    ),
  shortDescription: z
    .string()
    .describe(
      "120 characters or less. Manager-facing summary of the candidate's unanswered question and how long they've been waiting."
    ),
  descriptions: z.object({
    manager: z.object({
      description: z
        .string()
        .describe(
          "Summary of the unanswered candidate question and recruiter response gap. Written for a manager—observational, third-person perspective (e.g., 'The recruiter has not responded to...', 'The candidate asked...')."
        ),
      shortDescription: z
        .string()
        .describe(
          "120 characters or less. Manager-facing summary of the candidate's unanswered question and how long the recruiter has been unresponsive."
        ),
    }),
    recruiter: z.object({
      description: z
        .string()
        .describe(
          "Direct, actionable summary for the recruiter who owns this candidate. Use second person (e.g., 'Your candidate asked...', 'You have not responded...'). Focus on what the recruiter needs to do to address the question."
        ),
      shortDescription: z
        .string()
        .describe(
          "120 characters or less. Recruiter-facing summary of the candidate's unanswered question and the action you need to take."
        ),
    }),
  }),
});
export type QuestionDetectionResult = z.infer<typeof QuestionDetectionResult>;

export const RiskSignal = z.enum([
  'Communication Gaps',
  'Compensation Concerns',
  'Schedule/Shift Conflicts',
  'Family/Personal Issues',
  'Competing Offers',
  'Travel Conflicts',
]);
export type RiskSignal = z.infer<typeof RiskSignal>;

export const PlacementRiskAssessment = OpportunityDetectionBase.extend({
  justification: z
    .string()
    .describe(
      "Analyze the activity data and describe any potential risks or issues that may affect the candidate's likelihood of starting their assignment as planned. Provide specific details and/or direct quotes from the activity data to support your reasoning."
    ),
  confidence: ConfidenceScore.describe(
    'Now, based on your reasoning above, assign a risk score from 0-10 to indicate the likelihood of a negative outcome if the issue remains unresolved with the candidate. A score of 0 means the candidate is highly likely to begin working as planned, while a score of 10 means they are very unlikely to start their assignment as planned.'
  ),
  description: z
    .string()
    .describe(
      'Brief summary of what issues are occurring that lead to the risk calculation. Written for a manager at the recruitment agency—frame from an observational, third-person perspective.'
    ),
  shortDescription: z
    .string()
    .describe('120 characters or less. Manager-facing version of description.'),
  descriptions: z.object({
    manager: z.object({
      description: z
        .string()
        .describe(
          'Brief summary of the issues that led to the backout risk calculation. Written for a manager—observational, third-person perspective (e.g., "The candidate expressed...", "The recruiter has not...").'
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Manager-facing summary of the primary risk factors driving the backout risk score.'
        ),
    }),
    recruiter: z.object({
      description: z
        .string()
        .describe(
          "Direct, actionable summary of the placement risk for the recruiter who owns this candidate. Use second person (e.g., 'Your candidate may not start...', 'You should...'). Focus on what the recruiter needs to do to prevent the candidate from backing out."
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Recruiter-facing summary of the risk and the most important action to take.'
        ),
    }),
  }),
  riskSignal: RiskSignal.nullish().describe(
    'Categorize the primary type of risk signal detected based on the candidate behavior or situation.'
  ),
  riskDetails: z
    .string()
    .nullish()
    .describe(
      'Specific details about the risk factors and circumstances that could lead to the candidate backing out.'
    ),
});
export type PlacementRiskAssessment = z.infer<typeof PlacementRiskAssessment>;

export const PlacementFrustrationAssessment = OpportunityDetectionBase.extend({
  justification: z
    .string()
    .describe(
      'Analyze the activity data and identify any signs of candidate frustration that could lead them to quit or stop showing up for their assignment. Provide specific details and/or direct quotes from the activity data to support your reasoning.'
    ),
  confidence: ConfidenceScore.describe(
    'Now, based on your reasoning above, assign a risk score from 0-10 to indicate the likelihood that the candidate will quit or stop showing up for their assignment due to frustration, with a score of 0 being low risk and 10 being high risk of quitting.'
  ),
  description: z
    .string()
    .describe(
      'Brief summary of the issues causing frustration and the risk of the candidate quitting. Written for a manager at the recruitment agency—frame from an observational, third-person perspective.'
    ),
  shortDescription: z
    .string()
    .describe('120 characters or less. Manager-facing version of description.'),
  descriptions: z.object({
    manager: z.object({
      description: z
        .string()
        .describe(
          'Brief summary of the issues causing frustration and the risk of the candidate quitting. Written for a manager—observational, third-person perspective (e.g., "The candidate expressed frustration with...", "There is a risk the candidate may quit...").'
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Manager-facing summary of the frustration source and quit risk level.'
        ),
    }),
    recruiter: z.object({
      description: z
        .string()
        .describe(
          "Direct, actionable summary of the candidate's frustration for the recruiter who owns this candidate. Use second person (e.g., 'Your candidate is frustrated with...', 'You should...'). Focus on what the recruiter needs to do to address the frustration and retain the candidate."
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Recruiter-facing summary of the frustration and the most important action to take.'
        ),
    }),
  }),
  suggestions: z
    .array(z.string())
    .describe(
      "List of 1-3 suggestions for addressing the candidate's frustration based on the specific issues mentioned in the activity data."
    ),
});
export type PlacementFrustrationAssessment = z.infer<typeof PlacementFrustrationAssessment>;

export const LeadAbandonmentAssessment = OpportunityDetectionBase.extend({
  justification: z
    .string()
    .describe(
      'Analyze the activity data and identify signs that suggest this lead has been abandoned after a single contact attempt. Provide specific details and/or quotes from the activity data to support your reasoning.'
    ),
  confidence: ConfidenceScore.describe(
    'Now, based on your reasoning above, assign a risk score from 0-10 indicating the likelihood that this is a genuine lead abandonment situation, with a score of 0 being low confidence and 10 being high confidence that the lead has been abandoned after a single contact attempt.'
  ),
  description: z
    .string()
    .describe(
      'Brief summary of the situation, explaining why this appears to be a lead abandonment scenario. Written for a manager at the recruitment agency—frame from an observational, third-person perspective.'
    ),
  shortDescription: z
    .string()
    .describe('120 characters or less. Manager-facing version of description.'),
  descriptions: z.object({
    manager: z.object({
      description: z
        .string()
        .describe(
          'Brief summary of the lead abandonment scenario. Written for a manager—observational, third-person perspective (e.g., "The recruiter made a single outreach attempt...", "The lead has not been followed up with...").'
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Manager-facing summary of the abandonment situation and how long since the last outreach.'
        ),
    }),
    recruiter: z.object({
      description: z
        .string()
        .describe(
          "Direct, actionable summary of the lead abandonment for the recruiter who owns this candidate. Use second person (e.g., 'You have not followed up with...', 'You should re-engage...'). Focus on what the recruiter needs to do to re-engage the lead."
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Recruiter-facing summary of the abandoned lead and the action needed to re-engage.'
        ),
    }),
  }),
});
export type LeadAbandonmentAssessment = z.infer<typeof LeadAbandonmentAssessment>;

export const ResponseTimeAssessment = OpportunityDetectionBase.extend({
  justification: z
    .string()
    .describe(
      'Analyze the response time data and explain the significance of the change in response times. Consider the impact on candidate experience and engagement. Reference specific examples from the fastest and slowest response times provided.'
    ),
  confidence: ConfidenceScore.describe(
    'Assign a confidence score from 0-10 for the importance of this response time change, with 10 being critically important and 0 being insignificant.'
  ),
  description: z
    .string()
    .describe(
      'Brief summary explaining the response time trend and its potential impact on recruitment outcomes. Written for a manager at the recruitment agency—frame from an observational, third-person perspective.'
    ),
  shortDescription: z
    .string()
    .describe(
      '120 characters or less. Manager-facing version of description highlighting the key trend.'
    ),
  descriptions: z.object({
    manager: z.object({
      description: z
        .string()
        .describe(
          'Brief summary of the recruiter\'s response time trend and its potential impact on recruitment outcomes. Written for a manager—observational, third-person perspective (e.g., "The recruiter\'s response times have...", "This trend may impact...").'
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Manager-facing summary of the response time trend and its impact on candidate experience.'
        ),
    }),
    recruiter: z.object({
      description: z
        .string()
        .describe(
          "Direct, actionable summary of the response time trend for the recruiter being reviewed. Use second person (e.g., 'Your response times have...', 'You should...'). Focus on what the recruiter needs to know about their performance and how to improve."
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Recruiter-facing summary of the response time change and what it means for their performance.'
        ),
    }),
  }),
});
export type ResponseTimeAssessment = z.infer<typeof ResponseTimeAssessment>;

export const ReasonForLoss = z.enum([
  'Compensation & Benefits',
  'Work Flexibility',
  'Process & Communication',
  'Prior Experience/Relationship',
  'Active Competitor Engagement',
  'Other/Unknown',
]);
export type ReasonForLoss = z.infer<typeof ReasonForLoss>;

export const IncidentType = z.enum([
  'Physical Injury',
  'Needle Stick',
  'Assault/Violence',
  'Sexual Harassment',
  'Equipment/Safety Hazard',
  'Environmental Exposure',
  'Other Safety Concern',
]);
export type IncidentType = z.infer<typeof IncidentType>;

export const WorkersCompLegalRiskAssessment = OpportunityDetectionBase.extend({
  justification: z
    .string()
    .describe(
      'Analyze the activity data and identify any mentions of physical harm, safety incidents, assault, or workplace hazards that could require workers compensation or legal action. Provide specific details and direct quotes.'
    ),
  confidence: ConfidenceScore.describe(
    'Risk severity score from 0-10. Score 10 for confirmed serious incidents requiring immediate action. Score 0 when NO risk indicators are found. This is NOT a confidence score in your reasoning, but rather a risk severity score that can be used to determine whether the candidate should be contacted for further investigation.'
  ),
  description: z
    .string()
    .describe(
      'Brief summary of the safety incident or legal risk. Written for a manager at the recruitment agency—frame for immediate action by management.'
    ),
  shortDescription: z
    .string()
    .describe('120 characters or less. Manager-facing version of description.'),
  descriptions: z.object({
    manager: z.object({
      description: z
        .string()
        .describe(
          'Brief summary of the safety incident or legal risk. Written for a manager—frame for immediate action by management (e.g., "The candidate reported a workplace injury...", "This incident requires immediate escalation...").'
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Manager-facing summary of the incident type and urgency level.'
        ),
    }),
    recruiter: z.object({
      description: z
        .string()
        .describe(
          "Direct, actionable summary of the safety incident for the recruiter who owns this candidate. Use second person (e.g., 'Your candidate reported...', 'You should contact...'). Focus on the immediate steps the recruiter needs to take."
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Recruiter-facing summary of the incident and the urgent action required.'
        ),
    }),
  }),
  incidentType: IncidentType.describe(
    'Categorize the primary type of safety incident or legal risk.'
  ),
  incidentDetails: z
    .string()
    .describe(
      'Specific details about what happened, when, where, and any injuries or harm reported.'
    ),
  reportedToFacility: z
    .boolean()
    .nullish()
    .describe('Whether the candidate mentioned reporting this to the facility/supervisor.'),
});
export type WorkersCompLegalRiskAssessment = z.infer<typeof WorkersCompLegalRiskAssessment>;

export const CompetitorLossAssessment = OpportunityDetectionBase.extend({
  justification: z
    .string()
    .describe(
      'Analyze the activity data and identify signs that the candidate has accepted a position with a competing staffing company. Provide specific details and/or quotes from the activity data to support your reasoning.'
    ),
  confidence: ConfidenceScore.describe(
    'Now, based on your reasoning above, assign a confidence score from 0-10 indicating the likelihood that the candidate has accepted a position with a competing staffing company, with a score of 0 being low confidence and 10 being high confidence.'
  ),
  description: z
    .string()
    .describe(
      'Brief summary of the situation, explaining why this appears to be a loss to a competitor. Written for a manager at the recruitment agency—frame from an observational, third-person perspective.'
    ),
  shortDescription: z
    .string()
    .describe('120 characters or less. Manager-facing version of description.'),
  descriptions: z.object({
    manager: z.object({
      description: z
        .string()
        .describe(
          'Brief summary of the competitor loss situation. Written for a manager—observational, third-person perspective (e.g., "The candidate appears to have accepted a position with...", "The candidate is considering leaving for...").'
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Manager-facing summary of the competitor name (if known) and the likelihood of loss.'
        ),
    }),
    recruiter: z.object({
      description: z
        .string()
        .describe(
          "Direct, actionable summary of the competitor loss for the recruiter who owns this candidate. Use second person (e.g., 'Your candidate is considering a position with...', 'You should reach out to...'). Focus on what the recruiter needs to do to retain the candidate."
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Recruiter-facing summary of the competitor situation and the action needed to retain the candidate.'
        ),
    }),
  }),
  competitorName: z
    .string()
    .nullish()
    .describe(
      'The name of the competing staffing company, if mentioned in the activity data. If no specific company is mentioned, return null.'
    ),
  competitorCategory: z
    .enum(['KnownAgency', 'UnknownAgency', 'DirectStaffPosition'])
    .nullish()
    .describe(
      'Category of the competitor: KnownAgency for established staffing companies, UnknownAgency for generic/unknown agencies, DirectStaffPosition for direct hospital/facility employment.'
    ),
  // primaryReasonForLoss: z.enum(['Better Pay/Benefits', 'Location Preference', 'Scheduling Flexibility', 'Faster Placement Process', 'Better Communication', 'Previous Relationship', 'Other']).nullish()
  //     .describe('The main reason the candidate chose the competitor over your company.'),
  primaryReasonForLoss: ReasonForLoss.nullish().describe(
    'The main reason the candidate chose the competitor over your company.'
  ),
  allMentionedReasons: z
    .array(z.string())
    .nullish()
    .describe(
      'Array of all specific reasons mentioned by the candidate for choosing the competitor.'
    ),
  reasonDetails: z
    .string()
    .nullish()
    .describe(
      'Specific details about why they chose the competitor, including any comparisons made between companies or processes.'
    ),
});
export type CompetitorLossAssessment = z.infer<typeof CompetitorLossAssessment>;

export const PhoneScreenNoSubmissionReason = z.enum([
  'Candidate Not Interested',
  'No Matching Jobs Available',
  'Candidate Not Available',
  'Waiting for Specific Location',
  'Compensation Expectations Not Met',
  'Still In Process',
  'Unknown/No Clear Reason',
]);
export type PhoneScreenNoSubmissionReason = z.infer<typeof PhoneScreenNoSubmissionReason>;

export const PhoneScreenFollowUpAssessment = OpportunityDetectionBase.extend({
  justification: z
    .string()
    .describe(
      'Analyze the activity data after the phone screen to identify any reasons why the candidate has not been submitted to jobs. Look for candidate preferences, availability issues, or other factors that might explain the lack of submissions. Provide specific details and/or quotes from the activity data.'
    ),
  confidence: ConfidenceScore.describe(
    'Based on your reasoning above, assign a confidence score from 0-10 indicating how actionable this opportunity is. A score of 0 means there are clear reasons why submission is not appropriate, while 10 means the candidate appears interested and ready but simply needs follow-up.'
  ),
  description: z
    .string()
    .describe(
      'Brief summary of the situation, explaining why follow-up is needed after the phone screen. Written for a manager at the recruitment agency—frame from an observational, third-person perspective.'
    ),
  shortDescription: z
    .string()
    .describe('120 characters or less. Manager-facing version of description.'),
  descriptions: z.object({
    manager: z.object({
      description: z
        .string()
        .describe(
          'Brief summary of why follow-up is needed after the phone screen. Written for a manager—observational, third-person perspective (e.g., "The candidate was screened but has not been submitted...", "The recruiter has not followed up since the phone screen...").'
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Manager-facing summary of the phone screen follow-up gap and the primary reason for no submission.'
        ),
    }),
    recruiter: z.object({
      description: z
        .string()
        .describe(
          "Direct, actionable summary for the recruiter who owns this candidate. Use second person (e.g., 'Your candidate was screened but has not been submitted...', 'You should...'). Focus on the specific next step the recruiter should take to move the candidate forward."
        ),
      shortDescription: z
        .string()
        .describe(
          '120 characters or less. Recruiter-facing summary of the follow-up gap and the specific next action to take.'
        ),
    }),
  }),
  primaryReason: PhoneScreenNoSubmissionReason.describe(
    'The primary reason identified for why there has been no submission after the phone screen.'
  ),
  candidateInterestLevel: z
    .enum(['High', 'Medium', 'Low', 'Unknown'])
    .nullish()
    .describe(
      "Based on the activity data, assess the candidate's apparent interest level in finding a position."
    ),
  suggestedActions: z
    .array(z.string())
    .nullish()
    .describe(
      'List of 1-3 specific actions the recruiter should take based on the situation (e.g., "Check for new openings in Texas", "Discuss flexible scheduling options", etc.)'
    ),
  candidatePreferences: z
    .string()
    .nullish()
    .describe(
      'Any specific preferences or requirements mentioned by the candidate that might be affecting submission opportunities.'
    ),
});
export type PhoneScreenFollowUpAssessment = z.infer<typeof PhoneScreenFollowUpAssessment>;

export const HandoffFailureReason = z.enum([
  'No Follow-up Attempts',
  'Minimal Outreach',
  'Candidate Unresponsive',
  'Candidate Declined',
  'Active Communication',
  'Already Resolved',
  'Other',
]);
export type HandoffFailureReason = z.infer<typeof HandoffFailureReason>;

export const HandoffFailureAssessment = OpportunityDetectionBase.extend({
  justification: z
    .string()
    .describe(
      'Analyze the activity data after the handoff to understand if the candidate is being properly followed up with. Look for connection attempts, candidate responses, and any reasons for delay. Provide specific details and/or DIRECT QUOTES (in double quotation marks) from the activity data.'
    ),
  confidence: ConfidenceScore.describe(
    'Risk level (0-10) that the candidate will not proceed in the recruiting process due to this handoff failure. 10 = highest risk (no attempts, candidate waiting), 0 = no risk (legitimate reasons, active attempts).'
  ),
  description: z
    .string()
    .describe(
      'Summary of what happened in the handoff and whether follow-up attempts were made. Written for a manager at the recruitment agency—frame from an observational, third-person perspective.'
    ),
  shortDescription: z
    .string()
    .max(120)
    .describe(
      '120 characters or less. Manager-facing summary of the handoff status and any issues identified.'
    ),
  descriptions: z.object({
    manager: z.object({
      description: z
        .string()
        .describe(
          'Summary of the handoff status and whether follow-up attempts have been made. Written for a manager—observational, third-person perspective (e.g., "The handoff was initiated but the recruiter has not followed up...", "The candidate has not been contacted since the handoff...").'
        ),
      shortDescription: z
        .string()
        .max(120)
        .describe(
          '120 characters or less. Manager-facing summary of the handoff status and how long since the last follow-up attempt.'
        ),
    }),
    recruiter: z.object({
      description: z
        .string()
        .describe(
          "Direct, actionable summary of the handoff status for the recruiter responsible for following up. Use second person (e.g., 'Your candidate has not been contacted since the handoff...', 'You should reach out to...'). Focus on what the recruiter needs to do next."
        ),
      shortDescription: z
        .string()
        .max(120)
        .describe(
          '120 characters or less. Recruiter-facing summary of the handoff gap and the specific next action needed.'
        ),
    }),
  }),
  assignedRecruiterName: z
    .string()
    .nullable()
    .describe(
      'Name of the person the candidate was transferred/assigned to in the handoff note, if mentioned. Extract from patterns like "transferred to [Name]" or "scheduled call with [Name]". Return null if no specific person is mentioned.'
    ),
  primaryReason: HandoffFailureReason.describe(
    'Primary reason category for the current status of this handoff.'
  ),
});
export type HandoffFailureAssessment = z.infer<typeof HandoffFailureAssessment>;

export const InboundFollowUpAssessment = OpportunityDetectionBase.extend({
  justification: z
    .string()
    .describe(
      "Evaluate the candidate's inbound response to determine whether it warrants recruiter follow-up. Consider: Does the message contain actionable information, a correction, a question, interest, or a concern that a recruiter should address? How relevant is this message to the candidate's hiring process or job fit? Provide specific details and/or DIRECT QUOTES (in double quotation marks) from the activity data."
    ),
  confidence: ConfidenceScore.describe(
    'How important is it that a recruiter responds to this candidate (0-10). 10 = critical (candidate provided information that directly impacts their hiring, expressed strong interest, or raised a concern that needs addressing), 0 = no response needed (message is conversational, an acknowledgment, or does not require action).'
  ),
  description: z
    .string()
    .describe(
      "Summary of what the candidate communicated and why it matters for their hiring process. Frame from the recruitment agency's perspective."
    ),
  shortDescription: z
    .string()
    .max(120)
    .describe(
      'Brief summary (120 chars max) of what the candidate said and why follow-up is needed.'
    ),
});
export type InboundFollowUpAssessment = z.infer<typeof InboundFollowUpAssessment>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region Candidate Profiles

export const LocationPreference = z.object({
  city: z.string().nullish().describe('The city for this location, if applicable'),
  state: StateAbbreviation.describe('The state abbreviation for this location'),
  isWilling: nullishWithDefault(z.boolean().default(true)).describe(
    'Whether the candidate is willing to work in this location (false indicates they do NOT want to work here)'
  ),
  notes: z
    .string()
    .nullish()
    .describe(
      'The candidate\'s own words supporting this preference (e.g. their reply "Stevens point info"), plus any location-specific requirements or restrictions. If you cannot quote candidate words supporting this location, omit the entry entirely.'
    ),
});
export type LocationPreference = z.infer<typeof LocationPreference>;

export const ContactMethod = z.object({
  note: z
    .string()
    .describe(
      'Why/how the candidate gave this as their OWN contact method, plus any directive about it (e.g. "shared as their preferred work email", "asked us to use this number going forward"). State this reasoning before the value.'
    ),
  label: z
    .string()
    .nullish()
    .describe(
      'Short categorization of this method, if discernible (e.g. "work", "personal", "mobile", "LinkedIn", "GitHub").'
    ),
  value: z
    .string()
    .describe(
      "The candidate's own contact value, exactly as they expressed it (e.g. an email address, phone number, mailing address, website, or social media handle/URL). Do not reformat or normalize it."
    ),
  timestamp: z.iso
    .datetime({ offset: true, local: true })
    .nullish()
    .catch(null)
    .describe(
      'When the candidate shared this, from the source message timestamp, as an ISO 8601 datetime.'
    ),
});
export type ContactMethod = z.infer<typeof ContactMethod>;

const ContactMethods = z.object({
  email: ContactMethod.array()
    .nullish()
    .describe("The candidate's OWN email addresses that they provided."),
  phone: ContactMethod.array()
    .nullish()
    .describe("The candidate's OWN phone numbers that they provided."),
  address: ContactMethod.array()
    .nullish()
    .describe(
      "The candidate's OWN physical or mailing addresses that they provided, captured as free text in whatever form they expressed it. Include any self-provided address regardless of country or format, even when it lacks a postal code or street number (e.g. non-US formats, PO boxes, building or villa names, rural townlands)."
    ),
  website: ContactMethod.array()
    .nullish()
    .describe(
      "The candidate's OWN personal websites, portfolios, or other web addresses that they provided."
    ),
  socialMedia: ContactMethod.array()
    .nullish()
    .describe(
      "The candidate's OWN social media profiles or handles that they provided (e.g. LinkedIn, GitHub, X). Identify the platform in `label`."
    ),
});

const emptyStringToNull = z
  .literal('')
  .transform(() => null)
  .pipe(z.null());

export const CompensationCadence = z.enum([
  'hourly',
  'daily',
  'weekly',
  'bi-weekly',
  'monthly',
  'annually',
  'unknown',
]);
export type CompensationCadence = z.infer<typeof CompensationCadence>;

export const CompensationPreference = z.object({
  minimum: z
    .number()
    .describe(
      'The pay figure the candidate stated, or the low end of a range. If only one value was given, put it here.'
    ),
  maximum: z
    .number()
    .nullish()
    .describe('The high end of a range the candidate stated. Null if only one number was given.'),
  currency: z
    .string()
    .catch('USD')
    .describe(
      'Currency code or symbol exactly as expressed by the candidate (e.g. "€", "EUR", "GBP"). Default to "USD" if not specified.'
    ),
  cadence: CompensationCadence.nullish().describe(
    'Pay period exactly as the candidate stated it — "100k/yr" → "annually", "$60/hr" → "hourly", "$450/day" → "daily". Only infer when no cadence is stated or the cadence is unsupported: large round figures (e.g. "$45,000") default to "annually", small unlabeled rates (e.g. "$60") default to "hourly".'
  ),
  note: z
    .string()
    .nullish()
    .describe(
      'The candidate\'s own words stating this preference, plus the basis or conditions for it. Always include the work type if the candidate specified one (e.g. "contract rate", "permanent placement rate"). Other useful context: prior rate ("last contract paid this"), conditions ("flex if housing included").'
    ),
  mentionedAt: z.iso
    .datetime()
    .nullish()
    .catch(null)
    .describe(
      'ISO datetime of the message where the candidate stated this compensation, taken directly from the activity timestamp.'
    ),
});
export type CompensationPreference = z.infer<typeof CompensationPreference>;

export const ExtraCompensationItem = z.object({
  type: z.string().describe('Kind of extra comp, e.g. "equity", "bonus", "car allowance"'),
  note: z.string().nullish().describe('Any detail the candidate expressed about it'),
  mentionedAt: z.iso
    .datetime()
    .nullish()
    .catch(null)
    .describe('ISO datetime when this was mentioned'),
});
export type ExtraCompensationItem = z.infer<typeof ExtraCompensationItem>;

export const CandidateProfileSchemaName = z.enum([
  'CandidateProfileMedical',
  'CandidateProfileProfessional',
]);
export type CandidateProfileSchemaName = z.infer<typeof CandidateProfileSchemaName>;

const CandidateProfileBase = z.object({
  _schemaName: CandidateProfileSchemaName.nullish().describe('Schema type discriminator'),
  contactMethods: ContactMethods.nullish().describe(
    'Contact methods the candidate provided for reaching THEM directly. Only include methods belonging to the candidate themselves — never contact information for any third party mentioned (references, current/past employers, colleagues, recruiters).'
  ),
  compensationPreferences: z
    .array(CompensationPreference)
    .nullish()
    .catch(null)
    .describe(
      'Compensation the CANDIDATE stated they want or would accept — expressed by the candidate themselves, or reported as their ask (e.g. a recruiter note saying "she is looking for $2200/wk"). NEVER include rates a recruiter offered or advertised (job blasts, pay packages, position pitches) unless the candidate replied accepting or negotiating that specific rate. One entry per distinct work context (different work type or currency/region = new entry; same context revised = update existing). Numbers exactly as stated — never convert. "$60/hr for contract, $80k/yr for perm" → two entries. "100k/yr" → {minimum:100000, cadence:"annually"}. "€80-90k" → {minimum:80000, maximum:90000, currency:"EUR", cadence:"annually"}.'
    ),
  extraCompensation: z
    .array(ExtraCompensationItem)
    .nullish()
    .describe(
      'Non-salary compensation the CANDIDATE asked for or said they want (e.g. equity, signing bonus, car allowance, housing stipend) — never perks a recruiter offered unless the candidate engaged with them. One entry per distinct type.'
    ),
  customFields: z.looseObject({}).nullish(),
});
type CandidateProfileBase = z.infer<typeof CandidateProfileBase>;

export const CandidateProfileMedical = CandidateProfileBase.safeExtend({
  _schemaName: nullishWithDefault(
    z.literal('CandidateProfileMedical').default('CandidateProfileMedical')
  ).describe('Schema type discriminator'),
  specialty: z
    .object({
      licenseType: z
        .string()
        .nullish()
        .describe(
          'Primary licensed type (e.g. "Registered Nurse", "Occupational Therapist", "Medical Doctor")'
        ),
      name: z
        .string()
        .describe(
          'Primary specialization within license type (e.g., PICU, ICU, ER, Pediatric, Home Health)'
        ),
      yearsExperience: z.number().nullish().describe('Years of experience in primary specialty'),
    })
    .nullish(),
  statesLicensed: StateLicenseOption.array()
    .nullish()
    .describe('Array of state licenses held by candidate'),
  statesPreference: StateAbbreviation.array()
    .nullish()
    .describe(
      'States the CANDIDATE expressed desire to work in — never inferred from states recruiters pitched jobs in unless the candidate replied engaging with that location.'
    ), // TODO: remove this once the transformer is operational to parse out from locationsPreference
  shiftPreference: ShiftType.nullish().describe('Preferred shift schedule'),
  locationsPreference: z
    .array(LocationPreference)
    .nullish()
    .describe(
      'Locations the CANDIDATE expressed preference or willingness to work in, in their own words. Locations recruiters pitched (openings, job blasts) do NOT count unless the candidate replied engaging with that specific location — engaging with one city does not qualify other cities in the same message or thread. Each location should include a state abbreviation and optionally a specific city.'
    ),
  dateAvailable: z.iso
    .date()
    .or(emptyStringToNull)
    .nullish()
    .describe('Date available for next assignment (YYYY-MM-DD)'),
  qualifications: z
    .object({
      certifications: z.string().array().nullish().describe('Array of current certifications'),
      specialSkills: z.string().array().nullish().describe('Array of specific clinical skills'),
    })
    .nullish(),
  preferences: z
    .object({
      housing: z.string().nullish().describe('Housing requirements if specified'),
    })
    .nullish(),
});
export type CandidateProfileMedical = z.infer<typeof CandidateProfileMedical>;

// Alias for backwards compatibility
export const CandidateProfile = CandidateProfileMedical;
export type CandidateProfile = CandidateProfileMedical;

export const CandidateProfileProfessional = CandidateProfileBase.safeExtend({
  _schemaName: nullishWithDefault(
    z.literal('CandidateProfileProfessional').default('CandidateProfileProfessional')
  ).describe('Schema type discriminator'),
  relocation: z
    .string()
    .nullish()
    .describe(
      '"Yes" if the candidate has explicitly stated willingness to relocate, "No" if they have explicitly stated they will not relocate or are only interested in local opportunities. Omit if not discussed.'
    ),
  workPreference: z
    .enum(['Remote', 'Hybrid', 'On-Site'])
    .nullish()
    .catch(null) // `.catch(null)` keeps profiles written before this was an enum readable; they resolve to "not detected".
    .describe(
      '"Remote" if the candidate requires working from home, "Hybrid" if willing to come to the office some days per week, "On-Site" if willing to work from the office full-time. Base this on the candidate\'s own statements. Treat a willingness to give something up for an arrangement - lower pay, a longer commute, a less senior role - as a statement that the arrangement is their preference, ahead of one they merely work under today or would tolerate.'
    ),
  maxCommute: z
    .string()
    .nullish()
    .describe(
      'Maximum commute the candidate is willing to travel for in-office days, expressed as approximate drive time (e.g. "30 minutes", "1 hour"). If they mention a specific distance or cities, convert to estimated drive time.'
    ),
  availabilityToStart: z
    .string()
    .nullish()
    .describe(
      'When the candidate can begin a new role: "ASAP" if immediately available, a specific date in YYYY-MM-DD format if mentioned, or the notice period required (e.g. "2 weeks", "30 days", "1 month")'
    ),
  qualifications: z
    .object({
      certifications: z
        .string()
        .array()
        .nullish()
        .describe(
          'Professional certifications and licenses the candidate has confirmed they hold, including state-specific licenses where applicable (e.g. "CA Bar License", "TX Real Estate License"). Do not include degrees.'
        ),
      degrees: z
        .array(
          z.object({
            note: z
              .string()
              .describe(
                'Why/how the candidate is known to hold this degree (e.g. "listed under Education on the resume"). State this reasoning before the details.'
              ),
            degree: z
              .string()
              .nullish()
              .describe(
                'The degree or diploma earned (e.g. "Bachelor of Science", "MBA", "PhD", "Licence Sciences pour l\'ingénieur"), as stated'
              ),
            fieldOfStudy: z
              .string()
              .nullish()
              .describe('Major or field of study (e.g. "Civil Engineering"), if stated'),
            school: z.string().nullish().describe('Institution that granted the degree, if stated'),
            graduationYear: z
              .string()
              .nullish()
              .describe('Year or date the degree was completed or is expected, if stated'),
          })
        )
        .nullish()
        .describe(
          'Academic degrees or diplomas the candidate has earned, one entry per degree. Only formal academic qualifications — professional certifications and licenses belong in certifications, not here.'
        ),
      specialSkills: z
        .string()
        .array()
        .nullish()
        .describe(
          'Specialized technical or professional skills explicitly mentioned that demonstrate expertise beyond general job duties and are not represented by a certification (e.g. specific software proficiencies, methodologies, specialized techniques)'
        ),
    })
    .nullish(),
  securityClearance: z
    .string()
    .nullish()
    .describe(
      'Highest security clearance level if mentioned (e.g. "Secret", "Top Secret", "TS/SCI", "Q Clearance")'
    ),
  industry: z
    .object({
      name: z
        .string()
        .nullish()
        .describe('Industry/domain: IT, Healthcare, Finance, Manufacturing, etc.'),
      justification: z
        .string()
        .nullish()
        .describe('Brief evidence-based explanation for the industry classification'),
    })
    .nullish()
    .describe('Industry/domain based on DEMONSTRATED experience, not aspirations'),
  discipline: z
    .object({
      name: z
        .string()
        .nullish()
        .describe(
          'Functional area: Software Development, Nursing, Accounting, Project Management, etc.'
        ),
      justification: z
        .string()
        .nullish()
        .describe('Brief evidence-based explanation for the discipline classification'),
    })
    .nullish()
    .describe('Functional area based on DEMONSTRATED experience, not aspirations'),
  roleType: z
    .object({
      type: z
        .enum(['Individual Contributor', 'People Manager'])
        .nullish()
        .describe(
          'Look for evidence of direct reports (led/supervised/had direct reports) for People Manager. Job title "Manager" alone does not indicate People Manager.'
        ),
      justification: z
        .string()
        .nullish()
        .describe('Brief evidence-based explanation for the classification'),
    })
    .nullish()
    .describe(
      'Whether candidate is an Individual Contributor or People Manager based on DEMONSTRATED experience'
    ),
});
export type CandidateProfileProfessional = z.infer<typeof CandidateProfileProfessional>;

// Any combination of fields from any candidate profile type, without a specified _schemaName
// This is necessary when _schemaName is missing because the schemas have some overlapping fields
export const UnknownCandidateProfile = z
  .looseObject({
    ...CandidateProfileMedical.omit({ _schemaName: true }).shape,
    // Note that the `qualifications` field overlaps between the 2 schemas
    // and the Professional version takes precendence here,
    // but it's a superset of Medical's fields so that works out
    ...CandidateProfileProfessional.omit({ _schemaName: true }).shape,
  })
  .partial();
export type UnknownCandidateProfile = z.infer<typeof UnknownCandidateProfile>;

export const AnyCandidateProfile = z.discriminatedUnion('_schemaName', [
  CandidateProfileMedical.safeExtend({ _schemaName: z.literal('CandidateProfileMedical') }),
  CandidateProfileProfessional.safeExtend({
    _schemaName: z.literal('CandidateProfileProfessional'),
  }),
  UnknownCandidateProfile.safeExtend({ _schemaName: z.null().optional() }),
]);
export type AnyCandidateProfile = z.infer<typeof AnyCandidateProfile>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region  Candidate Profile calculated fields

export const BaseProfileCalculatedFields = z.object({
  desiredCompensation: CompensationPreference.nullish().describe(
    'Most recently mentioned compensationPreference with a set minimum value'
  ),
});
export type BaseProfileCalculatedFields = z.infer<typeof BaseProfileCalculatedFields>;

export const MedicalProfileCalculatedFields = BaseProfileCalculatedFields.safeExtend({
  nurseLicensureCompact: z
    .boolean()
    .nullish()
    .describe('True if candidate has nurse licensure compact eligibility based on statesLicensed'),
  citiesPreference: z
    .string()
    .array()
    .nullish()
    .describe('List of cities candidate is willing to work in based on locationsPreference'),
  citiesAndStatesPreference: z
    .string()
    .array()
    .nullish()
    .describe(
      'List of cities candidate is willing to work in based on locationsPreference, in "city, state" format'
    ),
});
export type MedicalProfileCalculatedFields = z.infer<typeof MedicalProfileCalculatedFields>;

// No professional-specific fields yet so this is the same as the base schema
export const ProfessionalProfileCalculatedFields = BaseProfileCalculatedFields;
export type ProfessionalProfileCalculatedFields = z.infer<
  typeof ProfessionalProfileCalculatedFields
>;

const calculateLocationsWithCities = (profile: AnyCandidateProfile) => {
  const locations = R.prop(profile, 'locationsPreference') || [];
  return locations
    .filter((loc) => R.isTruthy(loc.city))
    .filter((location) => location.isWilling !== false);
};
const calculateCitiesPreference = (profile: AnyCandidateProfile) =>
  R.pipe(
    profile,
    calculateLocationsWithCities,
    R.map(R.prop('city')),
    R.filter(R.isTruthy),
    R.unique()
  );
const calculateCitiesAndStatesPreference = (profile: AnyCandidateProfile) =>
  R.pipe(
    profile,
    calculateLocationsWithCities,
    R.map(({ city, state }) => `${city}, ${state}`),
    R.unique()
  );

// Calculate if candidate has nurse licensure compact eligibility
// Exclude 'Compact' itself when checking - only look at actual state licenses
const calculateNurseLicensureCompact = (profile: AnyCandidateProfile) =>
  R.prop(profile, 'statesLicensed')?.some(R.isIncludedIn(ENLC_STATES));

// Gets the most recent compensationPreference with a `minimum` value
const calculateDesiredCompensation = (profile: AnyCandidateProfile) =>
  R.pipe(
    R.prop(profile, 'compensationPreferences') || [],
    R.filter((pref) => R.isTruthy(pref.minimum)),
    R.firstBy([(x) => x.mentionedAt ?? '', 'desc'])
  ) || null;

function getBaseProfileCalculatedFields(profile: AnyCandidateProfile) {
  return {
    desiredCompensation: calculateDesiredCompensation(profile),
  };
}

function getMedicalProfileCalculatedFields(profile: CandidateProfileMedical) {
  return {
    ...getBaseProfileCalculatedFields(profile),
    nurseLicensureCompact: calculateNurseLicensureCompact(profile),
    citiesPreference: calculateCitiesPreference(profile),
    citiesAndStatesPreference: calculateCitiesAndStatesPreference(profile),
  };
}

export function getCalculatedCandidateProfileFields(profile: AnyCandidateProfile) {
  if (profile._schemaName === 'CandidateProfileMedical') {
    return getMedicalProfileCalculatedFields(profile);
  }
  return getBaseProfileCalculatedFields(profile);
}

// Calculates fields that are derived from other parts of the profile and adds them together
export function addCalculatedCandidateProfileFields<T extends AnyCandidateProfile>(profile: T) {
  return {
    ...profile,
    ...getCalculatedCandidateProfileFields(profile),
  };
}

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region  Prompt Types

export const CandidateInsightsProfilePromptType = z.enum([
  'candidate_profile',
  'candidate_profile_professional',
]);
export type CandidateInsightsProfilePromptType = z.infer<typeof CandidateInsightsProfilePromptType>;

export const AnalyticsPromptType = z.enum([
  'analyzeBackOutRiskOpportunities',
  'analyzeCompetitorOpportunities',
  'analyzeFollowUpOpportunities',
  'analyzeStrategicThemes',
]);
export type AnalyticsPromptType = z.infer<typeof AnalyticsPromptType>;

// TODO: Calculate these automatically from the opportunity types
export const OpportunityVerificationPromptType = z.enum([
  'opportunityVerification_followUpFailure',
  'opportunityVerification_leadAbandonment',
  'opportunityVerification_lossToCompetitor',
  'opportunityVerification_placementBackOutRisk',
  'opportunityVerification_placementFrustration',
  'opportunityVerification_phoneScreenFollowUp',
  'opportunityVerification_handoffFailure',
  'opportunityVerification_workersCompLegalRisk',
  'opportunityVerification_responseTimeImprovement',
  'opportunityVerification_responseTimeDecline',
  'opportunityVerification_inboundFollowUp',
]);
export type OpportunityVerificationPromptType = z.infer<typeof OpportunityVerificationPromptType>;

const { Candidate, Contact, Company } = ActivityDataEntityType.enum;

export const ActivityIqChatPromptType = z.enum([
  `askToro_chat_${Candidate}`,
  `askToro_chat_${Contact}`,
  `askToro_chat_${Company}`,
]);
export type ActivityIqChatPromptType = z.infer<typeof ActivityIqChatPromptType>;

export const ActivityIqFollowupSuggestionPromptType = z.enum([
  `askToro_followUpSuggestions_${Candidate}`,
  `askToro_followUpSuggestions_${Contact}`,
  `askToro_followUpSuggestions_${Company}`,
]);
export type ActivityIqFollowupSuggestionPromptType = z.infer<
  typeof ActivityIqFollowupSuggestionPromptType
>;

/** Entity-specific prompt types for AskToro chat (used by TalentIQ card without tool calling) */
export const ACTIVITY_IQ_CHAT_PROMPT_TYPES = {
  Candidate: 'askToro_chat_Candidate',
  Contact: 'askToro_chat_Contact',
  Company: 'askToro_chat_Company',
} as const satisfies Record<ActivityDataEntityType, ActivityIqChatPromptType>;

/** Entity-specific prompt types for follow-up suggestions */
export const ACTIVITY_IQ_FOLLOWUP_SUGGESTION_PROMPT_TYPES = {
  Candidate: 'askToro_followUpSuggestions_Candidate',
  Contact: 'askToro_followUpSuggestions_Contact',
  Company: 'askToro_followUpSuggestions_Company',
} as const satisfies Record<ActivityDataEntityType, ActivityIqFollowupSuggestionPromptType>;

/**
 * Master promptType for AskToro chat in chrome extension side panel.
 * Not linked to a specific entity.
 * Includes client-side tool calling (e.g. Chrome extension screenshots).
 * Includes additional context for Opportunity if provided.
 */
export const EXTENSION_CHAT_PROMPT_TYPE = 'askToro_chat_extension' as const;

export const ChatPromptType = z.enum(
  unique([...ActivityIqChatPromptType.options, EXTENSION_CHAT_PROMPT_TYPE])
);
export type ChatPromptType = z.infer<typeof ChatPromptType>;

export const ExtensionFollowupSuggestionsPromptType = z.enum([
  'askToro_followUpSuggestions',
  'askToro_followUpSuggestions_Opportunity',
]);
export type ExtensionFollowupSuggestionsPromptType = z.infer<
  typeof ExtensionFollowupSuggestionsPromptType
>;

export const FollowUpSuggestionsPromptType = z.enum([
  ...ActivityIqFollowupSuggestionPromptType.options,
  ...ExtensionFollowupSuggestionsPromptType.options,
]);
export type FollowUpSuggestionsPromptType = z.infer<typeof FollowUpSuggestionsPromptType>;

// TODO: remove the redundant `_data_iq` variants after fixing our caching logic for TalentIQ
//  (we'll need to filter for cached llmResponses that have a large enough date range)
export const DataIQProfilePromptType = z.enum([
  'candidate_profile_data_iq',
  'candidate_profile_professional_data_iq',
]);
export type DataIQProfilePromptType = z.infer<typeof DataIQProfilePromptType>;

export const CandidateProfilePromptType = z.enum([
  ...CandidateInsightsProfilePromptType.options,
  ...DataIQProfilePromptType.options,
]);
export type CandidateProfilePromptType = z.infer<typeof CandidateProfilePromptType>;

export const PromptType = z.enum([
  ...AnalyticsPromptType.options,
  ...OpportunityVerificationPromptType.options,
  ...ChatPromptType.options,
  ...FollowUpSuggestionsPromptType.options,
  ...CandidateProfilePromptType.options,
]);
export type PromptType = z.infer<typeof PromptType>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//

export const candidateInsightsProfileSchemas = {
  candidate_profile: CandidateProfile,
  candidate_profile_professional: CandidateProfileProfessional,
} as const satisfies Record<CandidateInsightsProfilePromptType, z.ZodSchema>;

export const candidateProfileSchemas = {
  ...candidateInsightsProfileSchemas,
  candidate_profile_data_iq: CandidateProfile,
  candidate_profile_professional_data_iq: CandidateProfileProfessional,
} as const satisfies Record<CandidateProfilePromptType, z.ZodSchema>;
export type CandidateProfileForPromptType<T extends CandidateProfilePromptType> = z.infer<
  (typeof candidateProfileSchemas)[T]
>;

export const candidateProfileExtraFieldSchemas = {
  candidate_profile: MedicalProfileCalculatedFields,
  candidate_profile_professional: ProfessionalProfileCalculatedFields,
  candidate_profile_data_iq: MedicalProfileCalculatedFields,
  candidate_profile_professional_data_iq: ProfessionalProfileCalculatedFields,
} as const satisfies Record<CandidateProfilePromptType, z.ZodSchema>;

export const ActivityLookBackConfig = z.object({
  initialLookBack: DurationLikeObject,
  increment: DurationLikeObject.optional(),
  incrementTo: DurationLikeObject.optional(),
});
export type ActivityLookBackConfig = z.infer<typeof ActivityLookBackConfig>;

export const CANDIDATE_PROFILE_LOOK_BACK_YEARS = {
  candidate_profile: 1,
  candidate_profile_professional: 10,
  candidate_profile_data_iq: 1,
  candidate_profile_professional_data_iq: 10,
} as const satisfies Record<CandidateProfilePromptType, number>;

export const ENTITY_LOOKBACK_CONFIGS = {
  Candidate: { initialLookBack: { years: 1 } },
  Contact: { initialLookBack: { years: 3 }, increment: { years: 7 }, incrementTo: { years: 10 } },
  Company: { initialLookBack: { years: 1 }, increment: { years: 9 }, incrementTo: { years: 10 } },
} as const satisfies Record<ActivityDataEntityType, ActivityLookBackConfig>;

export const DataIqUpdate = z.object({
  fieldUpdates: z
    .object({})
    .passthrough()
    .describe('Object containing field names as keys and their updated values'),
});
export type DataIqUpdate = z.infer<typeof DataIqUpdate>;

export const FieldIntelligence = z.object({
  uniqueIntelligence: z
    .string()
    .describe(
      'Unique conversational insight that reveals candidate-specific information not found in standard applications'
    ),
  candidateProfile: z
    .string()
    .describe('Brief candidate identifier (specialty, experience level, etc.)'),
  primaryQuote: z.string().describe('Most compelling direct quote supporting this intelligence'),
  businessImplication: z
    .string()
    .describe(
      'Concise, executive-level statement of strategic placement value (avoid lengthy explanations - focus on actionable business impact)'
    ),
  intelligenceScore: z.number().min(0).max(10).describe('Overall intelligence value score'),
  scoreBreakdown: z.object({
    uniqueness: z.number().min(0).max(10).describe('How rare/specific is this insight? (1-10)'),
    businessValue: z
      .number()
      .min(0)
      .max(10)
      .describe('How much does this help placement strategy? (1-10)'),
    emotionalResonance: z
      .number()
      .min(0)
      .max(10)
      .describe('How compelling is the human story? (1-10)'),
    dataValidity: z
      .number()
      .min(0)
      .max(10)
      .describe(
        'Is the field transformation valid and logical? (1-10, where 0 = nonsensical/wrong, 10 = perfectly valid)'
      ),
  }),
});
export type FieldIntelligence = z.infer<typeof FieldIntelligence>;

export const CandidateCaseStudyAnalysis = z.object({
  intelligence: z
    .array(
      z.object({
        fieldName: z
          .string()
          .describe(
            'Exact technical field name from fieldChanges array - must match exactly including case'
          ),
        ...FieldIntelligence.shape,
      })
    )
    .describe('Array of intelligence insights organized by field'),
});
export type CandidateCaseStudyAnalysis = z.infer<typeof CandidateCaseStudyAnalysis>;

export const CompetitorAnalyticsResult = z.object({
  // Single source of truth - everything organized by competitor
  competitors: z.array(
    z.object({
      name: z
        .string()
        .describe(
          'Normalized competitor name (e.g., group "Aya", "AYA", "aya healthcare" as "Aya Healthcare"). Use "Unknown Agency" for null/empty names.'
        ),
      category: z
        .enum(['KnownAgency', 'UnknownAgency', 'DirectStaffPosition'])
        .describe(
          'Category: KnownAgency for major staffing companies, UnknownAgency for generic/unknown agencies, DirectStaffPosition for direct hospital employment'
        ),

      // All opportunities lost to this competitor
      opportunityIds: z
        .array(ObjectIdString)
        .describe('All opportunity IDs lost to this competitor'),

      // Two-tier breakdown: themes containing detailed reasons
      reasonBreakdown: z
        .array(
          z.object({
            theme: ReasonForLoss.describe(
              'Thematic category: Compensation & Benefits, Work Flexibility, Process & Communication, Prior Experience/Relationship, Active Competitor Engagement, or Other/Unknown'
            ),
            opportunityIds: z.array(ObjectIdString).describe('All opportunity IDs for this theme'),
            detailedReasons: z
              .array(
                z.object({
                  reason: z
                    .string()
                    .describe(
                      'Specific reason within the theme (extract from text when "Other" is vague)'
                    ),
                  opportunityIds: z
                    .array(ObjectIdString)
                    .describe('Specific opportunity IDs for this detailed reason'),
                })
              )
              .describe('Detailed reasons within this theme with their associated opportunities'),
          })
        )
        .describe('Thematic breakdown of reasons with detailed drill-down capability'),
    })
  ),
  insights: z.array(
    z.string().describe('Actionable insights derived from the normalized analytics data')
  ),
});
export type CompetitorAnalyticsResult = z.infer<typeof CompetitorAnalyticsResult>;

const UrgencyLevel = z.enum(['Low', 'Medium', 'High']);

export const CandidateStage = z.enum(['Cold Lead', 'Hot Lead', 'On Assignment']);
export type CandidateStage = z.infer<typeof CandidateStage>;

export const FollowUpAnalyticsResult = z.object({
  recruiterBreakdown: z.array(
    z.object({
      name: z.string().describe('Normalized recruiter name'),
      candidateStatuses: z.array(
        z.object({
          status: CandidateStage.describe(
            'Candidate Status: Cold Lead, Hot Lead, or On Assignment'
          ),
          inquiryTypes: z.array(
            z.object({
              name: z.string().describe('Inquiry Type'),
              opportunityIds: z
                .array(ObjectIdString)
                .describe('Specific opportunity IDs for this inquiry type'),
            })
          ),
        })
      ),
    })
  ),
  urgencyBreakdown: z.array(
    z.object({
      name: UrgencyLevel.describe('Urgency Level: Low, Medium, or High'),
      value: z.number().describe('number of opportunities at this urgency level'),
    })
  ),
  insights: z.array(
    z
      .string()
      .describe(
        'Strategic insights and actionable recommendations derived from the structured follow-up data analysis'
      )
  ),
});
export type FollowUpAnalyticsResult = z.infer<typeof FollowUpAnalyticsResult>;

export const BackOutRiskAnalyticsResult = z.object({
  recruiterRiskBreakdown: z.array(
    z.object({
      name: z.string().describe('Normalized recruiter name'),
      riskSignals: z.array(
        z.object({
          name: z.string().describe('Risk Signal name'),
          opportunityIds: z
            .array(ObjectIdString)
            .describe('Specific opportunity IDs for this risk signal'),
        })
      ),
    })
  ),
  facilityRiskBreakdown: z.array(
    z.object({
      name: z.string().describe('Normalized hospital/facility name'),
      riskSignals: z.array(
        z.object({
          name: z.string().describe('Risk Signal name'),
          opportunityIds: z
            .array(ObjectIdString)
            .describe('Specific opportunity IDs for this risk signal'),
        })
      ),
    })
  ),
  insights: z.array(
    z
      .string()
      .describe(
        'Strategic insights and actionable recommendations derived from the structured back-out risk data analysis'
      )
  ),
});
export type BackOutRiskAnalyticsResult = z.infer<typeof BackOutRiskAnalyticsResult>;

export const allCandidateSchemas = {
  CandidateProfile,
  CandidateProfileProfessional,
  CandidateCaseStudyAnalysis,
} as const;

// Generate dynamic enum from analytics-enabled opportunity types

export const StrategicThemesAnalyticsResult = z.object({
  themes: z.array(
    z.object({
      id: z.string().describe('Unique identifier for the theme'),
      title: z.string().describe('Title of the strategic theme'),
      description: z.string().describe('Description of the theme and its importance'),
      priority: z
        .enum(['High', 'Medium', 'Low', 'Low-Medium'])
        .describe('Priority level of the theme'),
      color: z.string().describe('Color code for visual representation'),
      metrics: z.array(
        z.object({
          name: z.string().describe('Name of the metric'),
          value: z.string().describe('Current value of the metric'),
          status: z
            .enum(['critical', 'warning', 'success'])
            .describe('Status indicator for the metric'),
        })
      ),
      contributingFactors: z.array(
        z.object({
          name: z.string().describe('Name of the contributing opportunity type'),
          value: z.number().describe('Value/count for this factor'),
          // TODO: move stuff around so we don't have to declare this list a separate time here
          link: z
            .enum(['followUpFailure', 'placementBackOutRisk', 'lossToCompetitor'])
            .describe(
              'Link to detailed opportunity type analysis - must be one of the available analytics-enabled opportunity types'
            ),
        })
      ),
      recommendation: z.string().describe('High-level recommendation for addressing this theme'),
      actionItems: z
        .array(z.string())
        .describe('Specific action items to implement the recommendation'),
      impact: z.string().describe('Description of business impact'),
    })
  ),
  insights: z
    .array(z.string())
    .describe('High-level insights about the strategic themes and cross-cutting patterns'),
  summary: z.object({
    priorityDistribution: z.object({
      high: z.number().describe('Count of high-priority themes'),
      medium: z.number().describe('Count of medium-priority themes'),
      low: z.number().describe('Count of low-priority themes'),
    }),
  }),
});
export type StrategicThemesAnalyticsResult = z.infer<typeof StrategicThemesAnalyticsResult>;

export const allAnalyticsSchemas = {
  CompetitorAnalyticsResult,
  FollowUpAnalyticsResult,
  StrategicThemesAnalyticsResult,
  BackOutRiskAnalyticsResult,
} as const;

export const allOpportunityVerificationSchemas = {
  OpportunityDetectionBase,
  QuestionDetectionResult,
  PlacementRiskAssessment,
  PlacementFrustrationAssessment,
  LeadAbandonmentAssessment,
  CompetitorLossAssessment,
  ResponseTimeAssessment,
  PhoneScreenFollowUpAssessment,
  HandoffFailureAssessment,
  WorkersCompLegalRiskAssessment,
  InboundFollowUpAssessment,
} as const;
export type AnyOpportunityDetectionSchema =
  (typeof allOpportunityVerificationSchemas)[keyof typeof allOpportunityVerificationSchemas];
export type AnyOpportunityDetectionResult = z.infer<AnyOpportunityDetectionSchema>;

export const ChatFollowUpSuggestion = z.object({
  fullPrompt: z.string().describe('A complete, natural-language question about the entity'),
  shortPrompt: z
    .string()
    .describe('A very concise, less than 40 character rephrasing of the fullPrompt'),
});
export type ChatFollowUpSuggestion = z.infer<typeof ChatFollowUpSuggestion>;

export const ChatFollowUpSuggestionResponse = z.object({
  suggestedQuestions: ChatFollowUpSuggestion.array().describe(
    'A list of suggested follow-up questions the recruiter might want to ask.'
  ),
});

export const PromptSuggestion = ChatFollowUpSuggestion.extend({
  mediumPrompt: z.string().optional(), // for use in the preset prompt dropdown (not generated by LLM)
});
export type PromptSuggestion = z.infer<typeof PromptSuggestion>;

export type ChatFollowUpSuggestionResponse = z.infer<typeof ChatFollowUpSuggestionResponse>;
const askToroSchemas = {
  ChatFollowUpSuggestionResponse,
} as const;

export const allSchemas = {
  ...allOpportunityVerificationSchemas,
  ...allCandidateSchemas,
  ...allAnalyticsSchemas,
  ...askToroSchemas,
  DataIqUpdate,
} as const;
export type LlmResponseSchemaName = keyof typeof allSchemas;
export const allSchemaNames: Readonly<LlmResponseSchemaName[]> = keys(allSchemas);
// Update the enum to include our new schema
export const LLmResponseSchemaNameOption = z.enum(['OpportunityDetectionBase', ...allSchemaNames]);
export type LLmResponseSchemaNameOption = z.infer<typeof LLmResponseSchemaNameOption>;

export type LlmResponseSchema<T extends LlmResponseSchemaName = LlmResponseSchemaName> =
  (typeof allSchemas)[T];

export const isValidResponseSchemaName = R.isIncludedIn(allSchemaNames);

// LLM Response config for a prompt
export const LlmResponseSchemaConfig = z.object({
  /** Name of the internal schema. Must match one of our configured options in llmResponseSchema.ts */
  name: LLmResponseSchemaNameOption.nullable(),
  /** Label for the schema, to provide to the LLM */
  schemaLabel: z.string().optional(),
  /** Description of the whole schema, to provide to the LLM */
  schemaDescription: z.string().optional(),
  /** Object composed of individual field descriptions, to provide to the LLM */
  fieldDescriptions: NestedObjectOfStrings.optional(),
});
export type LlmResponseSchemaConfig<
  T extends LLmResponseSchemaNameOption | null = LLmResponseSchemaNameOption | null,
> = z.infer<typeof LlmResponseSchemaConfig> & { name: T };
